import { BasicTableConfig } from '@zspace/types';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Columns, Element } from 'react-bulma-components';
import If from '../../shared/if/if';
import SearchBar from '../search-bar/search-bar';
import styles from './inbox-table.module.scss';

export type InboxTableProps = {
  inbox?: ReactNode;
  table: ReactNode;
  isTableEmpty?: boolean;
  leftContent?: ReactNode;
  onSearchChange?: (value: string) => void;
  config?: BasicTableConfig;
  enableSearch?: boolean;
  errorMessage?: ReactNode;
};

export function InboxTable({
  inbox,
  table,
  isTableEmpty,
  leftContent,
  onSearchChange,
  config,
  enableSearch = true,
  errorMessage,
}: InboxTableProps) {
  const [search, setSearch] = useState(config?.search ?? '');

  const layoutClassName = useMemo(() => {
    if (!inbox && isTableEmpty) {
      return `${styles.layout}`;
    }
    return `${styles.layout} ${styles.borderedLayout} ${
      inbox ? `${styles.doubleColumnLayout}` : `${styles.singleColumnLayout}`
    }`;
  }, [inbox, isTableEmpty]);

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearch(value);
      onSearchChange?.(value);
    },
    [onSearchChange]
  );

  return (
    <>
      <Element display="flex" justifyContent="space-between" mb={3}>
        {leftContent}

        <Element
          display="flex"
          className="gap-3 w-full"
          justifyContent="flex-end"
        >
          <If condition={enableSearch}>
            <SearchBar
              onChange={handleSearchInputChange}
              searchValue={search}
              debounceMillis={1000}
            />
          </If>
        </Element>
      </Element>
      {errorMessage}
      <Columns marginless className={layoutClassName}>
        <If condition={!!inbox}>
          <Columns.Column
            mobile={{ size: 6 }}
            tablet={{ size: 5 }}
            desktop={{ size: 4 }}
            widescreen={{ size: 3 }}
            paddingless
            className={styles.leftColumn}
          >
            {inbox}
          </Columns.Column>
        </If>
        <Columns.Column
          className={inbox ? styles.rightColumn : styles.singleColumn}
          paddingless
        >
          {table}
        </Columns.Column>
      </Columns>
    </>
  );
}

export default InboxTable;
